/**
 * Copy the article's address, and SAY on screen that it happened.
 *
 * The front-end mirror of `useCopyLink()` in the reference's
 * `src/features/Blog/use-copy-link.ts`. Two blocks carry the button — `article-drawer` from
 * 1024 px up and `mobile-author-bar` below it — so the behaviour lives here instead of being
 * typed twice.
 *
 * WHY THIS EXISTS (founder, 2026-09-20)
 *   Both buttons called `navigator.clipboard.writeText()` and showed nothing. The founder
 *   tapped the one on his phone and reported the button as broken. It was not broken: it had
 *   copied the address and said nothing about it, and an action with no result on the screen is
 *   indistinguishable from an action that failed. The label now answers.
 *
 * THE FALLBACK
 *   `navigator.clipboard` is absent on an insecure origin and refuses inside a few embedded
 *   browsers (an in-app webview opened from a messenger is the case that matters for a link
 *   someone shares). The selection copy below is the one path those browsers still take, so the
 *   button reports a failure only when both have failed.
 *
 * THE WIDTH
 *   The label sits in a reserved box (`min-w-17`, 68 px, the widest of "Copy Link" at 66 px and
 *   "Copied" at 47 px in Inter 14/20), set in each block's `render.php`. A label that changed
 *   width mid-row would move the byline beside it.
 */

/** How long the answer stays on the button before it offers the action again. */
const RESET_MS = 2000;

/**
 * The pre-Clipboard-API copy: a textarea off-screen, selected, `execCommand('copy')`.
 *
 * @param {string} text The address to copy.
 * @return {boolean} Whether the browser took it.
 */
function copyThroughSelection( text ) {
	try {
		const field = document.createElement( 'textarea' );
		field.value = text;
		field.setAttribute( 'readonly', '' );
		field.style.position = 'fixed';
		field.style.top = '0';
		field.style.opacity = '0';
		document.body.appendChild( field );
		field.select();
		field.setSelectionRange( 0, text.length );
		const copied = document.execCommand( 'copy' );
		document.body.removeChild( field );
		return copied;
	} catch {
		return false;
	}
}

/**
 * Bind one Copy Link button.
 *
 * The button ships with its idle label in the HTML and the two answers in data attributes, so
 * every string stays translatable through PHP. The label node is `[data-ac-copy-label]` because
 * the drawer's button also holds an icon that must survive the swap.
 *
 * @param {HTMLElement} button The button carrying `data-ac-copy-link`.
 */
export function initCopyLink( button ) {
	const label = button.querySelector( '[data-ac-copy-label]' ) ?? button;
	const idleLabel = label.textContent;
	const doneLabel = button.dataset.copiedLabel ?? idleLabel;
	const failedLabel = button.dataset.failedLabel ?? idleLabel;
	let timer = null;

	const settle = ( text ) => {
		label.textContent = text;
		if ( timer ) {
			clearTimeout( timer );
		}
		timer = setTimeout( () => {
			label.textContent = idleLabel;
		}, RESET_MS );
	};

	button.addEventListener( 'click', () => {
		const url = window.location.href;
		if ( navigator.clipboard && navigator.clipboard.writeText ) {
			navigator.clipboard.writeText( url ).then(
				() => settle( doneLabel ),
				() =>
					settle(
						copyThroughSelection( url ) ? doneLabel : failedLabel
					)
			);
			return;
		}
		settle( copyThroughSelection( url ) ? doneLabel : failedLabel );
	} );
}
